import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

export interface CheckoutSessionResponse {
  url: string;
  payment_request_id: string;
}

export interface CreateCheckoutSessionPayload {
  productId: string;
}

async function createCheckoutSession(
  payload: CreateCheckoutSessionPayload,
): Promise<CheckoutSessionResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('לא מחובר. נסה שוב לאחר התחברות.');
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`;

  if (import.meta.env.DEV) {
    logger.warn('[PaymentHook] Initiating createCheckoutSession request:', {
      url,
      productId: payload.productId,
    });
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ productId: payload.productId }),
    });

    const contentType = res.headers.get('content-type') || '';
    let responseData: Record<string, unknown> | null = null;

    if (contentType.includes('application/json')) {
      responseData = await res.json() as Record<string, unknown>;
    } else {
      const text = await res.text();
      if (import.meta.env.DEV) {
        logger.error('[PaymentHook] createCheckoutSession returned non-JSON response:', undefined, {
          status: res.status,
          statusText: res.statusText,
          contentType,
          bodySnippet: text.substring(0, 500),
        });
      }
      throw new Error(`שגיאת שרת מרוחק (${res.status}): תגובת השרת אינה תקינה.`);
    }

    if (!res.ok) {
      if (import.meta.env.DEV) {
        logger.error('[PaymentHook] createCheckoutSession error response payload:', undefined, {
          status: res.status,
          statusText: res.statusText,
          error: responseData?.error || responseData,
        });
      }
      throw new Error((responseData?.error as string) || 'שגיאה באתחול תשלום.');
    }

    if (import.meta.env.DEV) {
      logger.warn('[PaymentHook] createCheckoutSession success response:', responseData);
    }

    return responseData as unknown as CheckoutSessionResponse;
  } catch (err: unknown) {
    if (import.meta.env.DEV) {
      logger.error('[PaymentHook] createCheckoutSession exception caught:', err instanceof Error ? err : new Error(String(err)));
    }
    throw err;
  }
}

export function useCreateCheckoutSession() {
  return useMutation<CheckoutSessionResponse, Error, CreateCheckoutSessionPayload>({
    mutationFn: createCheckoutSession,
  });
}

export interface CancelSubscriptionResponse {
  success: boolean;
  cancel_at: string;
  simulated?: boolean;
}

async function cancelSubscription(): Promise<CancelSubscriptionResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('לא מחובר. נסה שוב לאחר התחברות.');
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`;

  if (import.meta.env.DEV) {
    logger.warn('[PaymentHook] Dispatching cancel-subscription request to backend Edge Function:', {
      url,
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer [REDACTED_JWT_CLAIM]',
      },
    });
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    const contentType = res.headers.get('content-type') || '';
    let responseData: Record<string, unknown> | null = null;

    if (contentType.includes('application/json')) {
      responseData = await res.json() as Record<string, unknown>;
    } else {
      const text = await res.text();
      if (import.meta.env.DEV) {
        logger.error('[PaymentHook] cancelSubscription returned non-JSON response:', undefined, {
          status: res.status,
          statusText: res.statusText,
          contentType,
          bodySnippet: text.substring(0, 500),
        });
      }
      throw new Error(`שגיאת שרת מרוחק (${res.status}): תגובת השרת אינה תקינה.`);
    }

    if (!res.ok) {
      if (import.meta.env.DEV) {
        logger.error('[PaymentHook] cancelSubscription error response payload:', undefined, {
          status: res.status,
          statusText: res.statusText,
          error: responseData?.error || responseData,
        });
      }
      throw new Error((responseData?.error as string) || 'שגיאה בביטול המנוי.');
    }

    if (import.meta.env.DEV) {
      logger.warn('[PaymentHook] Received cancel-subscription response:', responseData);
    }

    return responseData as unknown as CancelSubscriptionResponse;
  } catch (err: unknown) {
    if (import.meta.env.DEV) {
      logger.error('[PaymentHook] cancelSubscription exception caught:', err instanceof Error ? err : new Error(String(err)));
    }

    // Differentiate network/CORS fetch failures from actual backend logic failures
    if (err && typeof err === 'object' && 'name' in err && 'message' in err && err.name === 'TypeError' && err.message === 'Failed to fetch') {
      const friendlyError = new Error(
        'שגיאת תקשורת (Failed to fetch). ייתכן שפונקציית הענן cancel-subscription טרם נפרסה לשרת המרוחק, או שישנה בעיית CORS. אנא ודא שביצעת פריסה עם supabase functions deploy.'
      );
      (friendlyError as unknown as { originalError: unknown }).originalError = err;
      throw friendlyError;
    }

    throw err;
  }
}

export function useCancelSubscription() {
  return useMutation<CancelSubscriptionResponse, Error, void>({
    mutationFn: cancelSubscription,
  });
}
